import React from "react";
import { Line } from 'react-chartjs-2';
import { Calendar, Download } from "lucide-react";

const SalesChart = ({
    salesRange,
    onSalesRangeChange,
    monthlyData,
    monthlyLabels,
    monthlyDataByYear,
    salesByDayLabels,
    salesByDayData,
    analytics,
    selectedYear,
    onYearChange,
    yearOptions,
}) => (
    <div className="bg-white shadow rounded-xl p-6 flex flex-col w-full">
        <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-800">Overall Sales</h2>
            <div className="flex items-center gap-2">
                <span className="flex items-center text-sm text-gray-600 font-medium">
                    <Calendar className="w-4 h-4 mr-1 text-primary" />
                    View:
                </span>
                <select
                    id="sales-range"
                    className="rounded-lg px-3 py-1.5 text-sm bg-white border border-gray-200 shadow-sm focus:outline-none hover:border-primary transition-colors"
                    value={salesRange}
                    onChange={e => onSalesRangeChange && onSalesRangeChange(e.target.value)}
                >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                </select>
                {salesRange === 'month' && yearOptions && yearOptions.length > 0 && (
                    <select
                        id="sales-year"
                        className="rounded-lg px-3 py-1.5 text-sm bg-white border border-gray-200 shadow-sm focus:outline-none hover:border-primary transition-colors"
                        value={selectedYear}
                        onChange={e => onYearChange && onYearChange(Number(e.target.value))}
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                )}
                <button
                    type="button"
                    className="ml-2 p-2 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                    title="Download sales data"
                    aria-label="Download sales data"
                    onClick={() => {
                        const normalizeDate = (value) => {
                            if (!value) return null;
                            const parsed = new Date(value);
                            if (isNaN(parsed)) return null;
                            return parsed.toISOString().slice(0, 10);
                        };

                        // Improved CSV export logic
                        let csvRows = [];
                        if (salesRange === 'month' && monthlyDataByYear && monthlyDataByYear.length) {
                            csvRows.push(`Month,Sales${selectedYear ? ` (${selectedYear})` : ''}`);
                            const months = monthlyLabels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                            for (let i = 0; i < monthlyDataByYear.length; i++) {
                                csvRows.push(`${months[i]},${monthlyDataByYear[i]}`);
                            }
                        } else if (salesByDayLabels && salesByDayLabels.length && salesByDayData && salesByDayData.length) {
                            // Try to include orders count if available in analytics.salesByDay
                            let hasOrders = false;
                            let ordersArr = [];
                            if (analytics && analytics.salesByDay) {
                                const ordersByDate = analytics.salesByDay.reduce((acc, d) => {
                                    const key = normalizeDate(d.date);
                                    if (!key) return acc;
                                    acc[key] = d.ordersCount !== undefined ? d.ordersCount : '';
                                    return acc;
                                }, {});

                                ordersArr = salesByDayLabels.map((label) => {
                                    const key = normalizeDate(label);
                                    return key && Object.prototype.hasOwnProperty.call(ordersByDate, key)
                                        ? ordersByDate[key]
                                        : '';
                                });
                                hasOrders = ordersArr.some(v => v !== '');
                            }
                            csvRows.push(`Date,Sales${hasOrders ? ',Orders' : ''}`);
                            for (let i = 0; i < salesByDayLabels.length; i++) {
                                // Format date as YYYY-MM-DD for clarity
                                const formattedDate = normalizeDate(salesByDayLabels[i]) || salesByDayLabels[i];
                                let row = `${formattedDate},${salesByDayData[i]}`;
                                if (hasOrders) row += `,${ordersArr[i] ?? ''}`;
                                csvRows.push(row);
                            }
                        }
                        const csvContent = csvRows.join('\n');
                        const blob = new Blob([csvContent], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `sales-${salesRange}${salesRange === 'month' && selectedYear ? `-${selectedYear}` : ''}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                    }}
                >
                    <Download className="w-4 h-4 text-primary" />
                </button>
            </div>
        </div>
        {salesRange === 'week' && (
            <div className="mb-2 text-right text-sm text-primary font-semibold">
                {(() => {
                    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                    const today = new Date();
                    let idx = today.getDay();
                    idx = idx === 0 ? 6 : idx - 1; // 0=Mon, 6=Sun
                    return `Today: ${weekDays[idx]}`;
                })()}
            </div>
        )}
        <div className="w-full h-80">
            <Line data={{
                labels:
                    salesRange === 'month'
                        ? (monthlyLabels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"])
                        : salesRange === 'week'
                            ? (() => {
                                // Mon-Sun labels
                                return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                            })()
                            : salesByDayLabels,
                datasets: [
                    {
                        label: `Sales (${salesRange.charAt(0).toUpperCase() + salesRange.slice(1)})`,
                        data:
                            salesRange === 'month'
                                ? (monthlyDataByYear || monthlyData)
                                : salesRange === 'week'
                                    ? (() => {
                                        // Map salesByDayData to Mon-Sun order
                                        const dataByDay = Array(7).fill(0);
                                        salesByDayLabels.slice(0, 7).forEach((date, i) => {
                                            const d = new Date(date);
                                            let idx = d.getDay();
                                            idx = idx === 0 ? 6 : idx - 1; // 0=Mon, 6=Sun
                                            dataByDay[idx] = salesByDayData[i];
                                        });
                                        return dataByDay;
                                    })()
                                    : salesByDayData,
                        borderColor: "#6366F1",
                        backgroundColor: "rgba(99,102,241,0.08)",
                        pointBackgroundColor: (ctx) => {
                            // Highlight current day in week view
                            if (salesRange === 'week') {
                                const today = new Date();
                                let idx = today.getDay();
                                idx = idx === 0 ? 6 : idx - 1; // 0=Mon, 6=Sun
                                return ctx.dataIndex === idx ? "#F59E42" : "#6366F1";
                            }
                            return "#6366F1";
                        },
                        pointBorderColor: "#6366F1",
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: true,
                        tension: 0.5,
                    },
                    {
                        label: 'Last Period',
                        data:
                            salesRange === 'month'
                                ? (monthlyDataByYear || monthlyData).map((v, i, arr) => arr[i - 1] ?? v)
                                : salesRange === 'week'
                                    ? (() => {
                                        // Map previous period data to Mon-Sun order
                                        const dataByDay = Array(7).fill(0);
                                        salesByDayLabels.slice(0, 7).forEach((date, i) => {
                                            const d = new Date(date);
                                            let idx = d.getDay();
                                            idx = idx === 0 ? 6 : idx - 1; // 0=Mon, 6=Sun
                                            dataByDay[idx] = i > 0 ? salesByDayData[i - 1] : salesByDayData[i];
                                        });
                                        return dataByDay;
                                    })()
                                    : salesByDayData.map((v, i, arr) => arr[i - 1] ?? v),
                        borderColor: "#D1D5DB",
                        backgroundColor: "rgba(209,213,219,0.15)",
                        pointBackgroundColor: "#D1D5DB",
                        pointBorderColor: "#D1D5DB",
                        pointRadius: 0,
                        fill: true,
                        tension: 0.5,
                    },
                ],
            }} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    title: { display: false },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#222',
                        bodyColor: '#222',
                        borderColor: '#e5e7eb',
                        borderWidth: 1,
                        titleFont: { size: 13, weight: 'bold' },
                        bodyFont: { size: 13 },
                        padding: 10,
                    },
                },
                scales: {
                    x: {
                        title: { display: true, text: salesRange === 'month' ? 'Month' : 'Day', font: { size: 13 }, color: '#6B7280' },
                        grid: {
                            display: true,
                            color: '#F3F4F6',
                            borderDash: [4, 4],
                            lineWidth: 1,
                        },
                        ticks: { font: { size: 13 }, color: '#6B7280' },
                    },
                    y: {
                        title: { display: true, text: 'Sales (₱)', font: { size: 13 }, color: '#6B7280' },
                        grid: {
                            display: false,
                        },
                        ticks: { font: { size: 13 }, color: '#6B7280', callback: value => `₱${value}` },
                    },
                },
            }} />
        </div>
    </div>
);

export default SalesChart;
